<template>
  <!-- Manages an OL VectorTile layer + a map popup overlay for selected overpass -->
  <Teleport to="body">
    <div
      ref="popupEl"
      class="map-kit-overpass-popup"
      :class="popupPlacementClass"
      v-show="selectedPassId && selectedPassData && popupVisible"
      :style="popupStyle"
    >
      <button class="map-kit-overpass-popup-close" @click="closePopup">&times;</button>
      <div v-if="selectedPassData" class="map-kit-overpass-popup-body">
        <strong>{{ selectedPassDisplayName }}</strong>
        <table class="map-kit-overpass-popup-table">
          <tbody>
            <tr><td>{{ $t('popup.start') }}</td><td>{{ fmtTime(selectedPassData.start_time) }}</td></tr>
            <tr><td>{{ $t('popup.end') }}</td><td>{{ fmtTime(selectedPassData.end_time) }}</td></tr>
            <tr v-if="selectedPassData.direction"><td>{{ $t('popup.direction') }}</td><td>{{ selectedPassData.direction === 'ASCENDING' ? $t('popup.ascending') : $t('popup.descending') }}</td></tr>
            <tr v-if="selectedPassData.sensor_type"><td>{{ $t('popup.sensor') }}</td><td>{{ selectedPassData.sensor_type }}</td></tr>
            <tr v-if="selectedPassData.spatial_res_m != null"><td>{{ $t('popup.resolution') }}</td><td>{{ selectedPassData.spatial_res_m }}m</td></tr>
            <!-- Swaths are thousands of km wide, so one cloud figure averaged across
                 them says nothing useful; it is omitted rather than shown as an
                 always-empty row. Cloud stays on AOIs and hex cells, where the
                 footprint is small enough for the number to mean something. -->
            <tr v-if="selectedPassData.cloud_cover_pct != null">
              <td>{{ $t('popup.cloudCoverForecast') }}</td>
              <td>{{ selectedPassData.cloud_cover_pct }}%</td>
            </tr>
            <tr v-if="selectedPassData.constellation"><td>{{ $t('popup.constellation') }}</td><td>{{ selectedPassData.constellation }}</td></tr>
            <tr v-if="selectedPassData.is_daytime != null"><td>{{ $t('popup.daylight') }}</td><td>{{ selectedPassData.is_daytime ? $t('popup.dayEmoji') : $t('popup.nightEmoji') }}</td></tr>
            <tr v-if="selectedPassLatency != null"><td>{{ $t('popup.expectedLatency') }}</td><td>~{{ Math.round(selectedPassLatency) }} min</td></tr>
            <tr v-if="selectedPassDelivery"><td>{{ $t('popup.expectedDelivery') }}</td><td>{{ fmtTime(selectedPassDelivery) }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, useTemplateRef, nextTick } from 'vue';
import Feature from 'ol/Feature';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { Fill, Stroke, Style } from 'ol/style';
import type { MapBrowserEvent } from 'ol';

import { toLonLat } from 'ol/proj';
import { useMap } from '@/composables/useMap';
import { useFilterStore, type VisiblePass } from '@/composables/useFilterStore';
import {
  collapseOroraConstellationPasses,
  getSatelliteDisplayName,
  groupedPassContainsRawId,
  isOroraTechConstellationSatellite,
} from '@/composables/useConstellationGrouping';
import { SAT_NORAD_MAP, getSatMetadata, resolveSatelliteName } from '@/composables/useAoiStore';
import { clipTimesToAoi } from '@/composables/useAoiCoverageBuckets';
import { computeIsDaytime } from '@/composables/useSolarPosition';
import { parseUtcEpochMs, useDateTimeSettings } from '@/composables/useDateTimeSettings';
import { getTimingColorForHours, getTimingOrderRank, NO_UPCOMING_HOURS } from '@/composables/useTimingColorScale';
import { isColorblindFriendly } from '@/composables/useAccessibilitySettings';
import {
  useSatelliteLatency,
  computeExpectedDeliveryForPass,
  getExpectedLatencyMinutesForPass,
} from '@/composables/useSatelliteLatency';
import { authFetch } from '@/composables/useAuthFetch';
import { normalizePassDirection } from '@/composables/passDirection';

const { map } = useMap();
useSatelliteLatency(); // trigger load on mount

const {
  timeRange,
  metaTimeRange,
  standardForecastEndMs,
  metadata,
  satelliteRoster,
  isQueryableSatellite,
  resolveServedNoradIds,
  swathWidthKmForNorad,
  selectedSatelliteNames,
  featurePassesFilter,
  visiblePasses,
  visiblePassCount,
  swathGraphPasses,
  selectedPassId,
  hoveredPassId,
  selectedPassSourceIds,
  hoveredPassSourceIds,
  setSelectedPass,
  setHoveredPass,
  selectedPass,
  overpassLayerVisible,
  overpassLayerOpacity,
  mapViewMode,
  ororaConstellationExpanded,
  otDataSourceFilter,
  cloudCoverThreshold,
  passWithinForecastHorizon,
  setSwathLoading,
} = useFilterStore();

const popupEl = useTemplateRef('popupEl');
const { formatDateTime } = useDateTimeSettings();

/* ── Styles ── */
const swathStyleCache = new Map<string, Style>();

function getSwathHoursToPass(props: Record<string, unknown>): number {
  const startMs = toMs(
    props.viewport_start_time ?? props.start_time ?? props.startTime ?? props.start,
  );
  if (!Number.isFinite(startMs)) return NO_UPCOMING_HOURS;
  return Math.max(0, (startMs - timeRange.value[0]) / 3_600_000);
}

function getSwathStyle(
  props: Record<string, unknown>,
  state: 'base' | 'hover' | 'selected' | 'dimmed',
): Style {
  const hours = getSwathHoursToPass(props);
  const rank = getTimingOrderRank(hours);
  // hover/selected render on selectedLayer (opacity 1.0) — bake layer opacity into fill alpha
  const layerAlpha = (state === 'hover' || state === 'selected') ? overpassLayerOpacity.value : 1;
  const fillAlpha = state === 'dimmed' ? 0 : layerAlpha;
  const unavailableAlpha = state === 'dimmed' ? 0 : layerAlpha;
  const fillColor = getTimingColorForHours(hours, fillAlpha, unavailableAlpha);
  const key = `${state}-${fillColor}-${rank}`;
  let style = swathStyleCache.get(key);
  if (!style) {
    let stroke = new Stroke({ color: 'rgba(15, 23, 42, 0.0)', width: 0.0 });
    let zIndex = rank;
    if (state === 'hover') {
      zIndex = 1200 + rank;
    } else if (state === 'selected') {
      zIndex = 1300 + rank;
    }
    style = new Style({
      fill: new Fill({ color: fillColor }),
      stroke,
      zIndex,
    });
    swathStyleCache.set(key, style);
  }
  return style;
}

/* ── Layer ── */
const swathSource = new VectorSource();

const baseLayer = new VectorLayer({
  source: swathSource,
  declutter: false,
  style: (feature) => {
    const props = feature.getProperties();
    if (!featurePassesFilter(props)) return undefined;
    const currentSelectedPasses = selectedPassesForInteraction.value;
    const currentSelectedContextPasses = selectedContextPassesForInteraction.value;
    const hasSelected = currentSelectedPasses.length > 0;
    if (hasSelected && isFeatureWithinAnyPass(props, currentSelectedPasses)) {
      return getSwathStyle(props, 'base');
    }
    if (hasSelected) {
      if (isFeatureWithinAnyPass(props, currentSelectedContextPasses)) {
        return getSwathStyle(props, 'base');
      }
      return getSwathStyle(props, 'dimmed');
    }
    return getSwathStyle(props, 'base');
  },
});

const selectedLayer = new VectorLayer({
  source: swathSource,
  declutter: false,
  style: (feature) => {
    const props = feature.getProperties();
    if (!featurePassesFilter(props)) return undefined;
    const currentSelectedPasses = selectedPassesForInteraction.value;
    if (currentSelectedPasses.length && isFeatureWithinAnyPass(props, currentSelectedPasses)) {
      return getSwathStyle(props, 'selected');
    }
    const currentHoveredPasses = hoveredPassesForInteraction.value;
    if (currentHoveredPasses.length && isFeatureWithinAnyPass(props, currentHoveredPasses)) {
      return getSwathStyle(props, 'hover');
    }
    return undefined;
  },
});

baseLayer.setZIndex(60);
selectedLayer.setZIndex(61);

/* ── Layer visibility ── */
const effectiveVisible = computed(() => overpassLayerVisible.value && mapViewMode.value === 'swath');

/* ── Fetch swaths: live predictor + scheduled (API) ── */
let swathFetchController: AbortController | null = null;
let swathRequestGeneration = 0;
const geoJsonFormat = new GeoJSON();
const LIVE_SWATH_CHUNK_MS = 24 * 60 * 60 * 1000;
const LIVE_SWATH_CONCURRENCY = 3;

// Predicted swaths are fetched from the range-aware live endpoint, so the map only
// loads the selected time window. Scheduled FOREST footprints are fetched alongside it.

async function fetchPredictedWindow(
  rangeStartMs: number,
  rangeEndMs: number,
  signal: AbortSignal,
): Promise<Feature[]> {
  if (!Number.isFinite(rangeStartMs) || !Number.isFinite(rangeEndMs) || rangeEndMs <= rangeStartMs) return [];

  const noradIds = resolveServedNoradIds(metadata.value?.satellites ?? []);
  if (!noradIds.length) return [];

  const params = new URLSearchParams({
    start: new Date(rangeStartMs).toISOString().replace(/\.\d{3}Z$/, 'Z'),
    end: new Date(rangeEndMs).toISOString().replace(/\.\d{3}Z$/, 'Z'),
    norad_ids: noradIds.join(','),
  });

  const response = await authFetch(`/api/swath?${params.toString()}`, { signal });
  if (!response.ok) {
    console.error(`Live swath fetch failed: ${response.status}`);
    return [];
  }
  const document = await response.json() as { features?: Record<string, unknown>[] };
  const rawFeatures = Array.isArray(document.features) ? document.features : [];

  const enriched: Record<string, unknown>[] = [];
  for (const feature of rawFeatures) {
    const props = (feature.properties ?? {}) as Record<string, unknown>;
    const satName = resolveSatelliteName(props);
    if (!satName) continue;
    const meta = getSatMetadata(satName);
    const startMs = parseUtcEpochMs(String(props.start_time ?? ''));
    const endMs = parseUtcEpochMs(String(props.end_time ?? ''));
    const centroid = geometryCentroidLonLat(feature.geometry as Record<string, unknown> | undefined);
    // Swath extents come from the roster, not the feature: /swath omits
    // swath_starboard_km and swath_port_km unless they are asked for by name, so
    // reading them here silently produced 0 for every feature. The roster carries
    // the same calibrated values once per satellite rather than once per feature.
    const noradId = Number(props.norad_id ?? props.norad_cat_id ?? NaN);
    const swathKm = Number.isFinite(noradId) ? swathWidthKmForNorad(noradId) : null;

    enriched.push({
      ...feature,
      properties: {
        satellite: satName,
        start_time: props.start_time,
        end_time: props.end_time,
        constellation: meta?.constellation ?? satName,
        operator: meta ? undefined : 'Various',
        sensor_type: meta?.sensor_type,
        spatial_res_m: meta?.spatial_res_m,
        swath_km: swathKm,
        tasking: false,
        data_source: 'api',
        direction: normalizePassDirection(props.direction),
        times_exact: props.times_exact === true,
        cloud_cover_pct: null,
        is_daytime: centroid
          ? computeIsDaytime(startMs, endMs, centroid[1], centroid[0])
          : undefined,
      },
    });
  }

  return geoJsonFormat.readFeatures(
    { type: 'FeatureCollection', features: enriched },
    { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' },
  ) as Feature[];
}

function splitSwathRange(rangeStartMs: number, rangeEndMs: number): Array<readonly [number, number]> {
  const windows: Array<readonly [number, number]> = [];
  for (let startMs = rangeStartMs; startMs < rangeEndMs; startMs += LIVE_SWATH_CHUNK_MS) {
    windows.push([startMs, Math.min(startMs + LIVE_SWATH_CHUNK_MS, rangeEndMs)]);
  }
  return windows;
}

async function fetchScheduledLive(
  rangeStartMs: number,
  rangeEndMs: number,
  signal: AbortSignal,
): Promise<Feature[]> {
  // Scheduled footprints come from GCS, not the predictor, so this deliberately
  // resolves against the whole known catalogue rather than the served roster — a
  // satellite the predictor has not picked up yet can still have tasked acquisitions.
  // isQueryableSatellite still applies: constellation members with no checkbox are
  // kept out of this request too, so they cannot appear on the map unfilterable.
  const noradIds = (metadata.value?.satellites ?? [])
    .filter((name) => isOroraTechConstellationSatellite(name) && isQueryableSatellite(name))
    .map((name) => satelliteRoster.value?.byName.get(name.toUpperCase()) ?? SAT_NORAD_MAP[name])
    .filter((id): id is number => id != null);
  if (!noradIds.length) return [];

  const params = new URLSearchParams({
    start: new Date(rangeStartMs).toISOString(),
    end: new Date(rangeEndMs).toISOString(),
    norad_ids: noradIds.join(','),
  });
  const response = await authFetch(`/api/scheduled-swaths?${params.toString()}`, { signal });
  if (!response.ok) {
    console.warn(`Scheduled swath fetch failed: ${response.status}`);
    return [];
  }
  const document = await response.json();
  return geoJsonFormat.readFeatures(document, {
    dataProjection: 'EPSG:4326',
    featureProjection: 'EPSG:3857',
  }) as Feature[];
}

/** Mean of a polygon's first ring — enough to place a swath for a day/night test. */
function geometryCentroidLonLat(geometry: Record<string, unknown> | undefined): [number, number] | null {
  if (!geometry) return null;
  const coordinates = geometry.coordinates as unknown;
  let ring: unknown = null;
  if (geometry.type === 'Polygon' && Array.isArray(coordinates)) ring = coordinates[0];
  else if (geometry.type === 'MultiPolygon' && Array.isArray(coordinates)) {
    ring = Array.isArray(coordinates[0]) ? (coordinates[0] as unknown[])[0] : null;
  }
  if (!Array.isArray(ring) || !ring.length) return null;
  let sumLon = 0;
  let sumLat = 0;
  let count = 0;
  for (const position of ring) {
    if (!Array.isArray(position) || position.length < 2) continue;
    sumLon += Number(position[0]);
    sumLat += Number(position[1]);
    count += 1;
  }
  return count ? [sumLon / count, sumLat / count] : null;
}

async function fetchSwaths() {
  if (swathFetchController) swathFetchController.abort();
  const requestGeneration = ++swathRequestGeneration;
  swathFetchController = new AbortController();
  const signal = swathFetchController.signal;
  setSwathLoading(true);

  if (!metadata.value?.satellites?.length) {
    swathSource.clear();
    scheduleCollect();
    setSwathLoading(false);
    return;
  }

  const [rangeStartMs, rangeEndMs] = timeRange.value;
  if (!Number.isFinite(rangeStartMs) || !Number.isFinite(rangeEndMs) || rangeEndMs <= rangeStartMs) {
    swathSource.clear();
    scheduleCollect();
    setSwathLoading(false);
    return;
  }

  swathSource.clear();
  scheduleCollect();

  const appendFeatures = (features: Feature[]) => {
    if (!features.length || signal.aborted || requestGeneration !== swathRequestGeneration) return;
    swathSource.addFeatures(features);
    scheduleCollect();
  };

  try {
    const windows = splitSwathRange(rangeStartMs, rangeEndMs);
    let nextWindowIndex = 0;
    const predictedWorkers = Array.from(
      { length: Math.min(LIVE_SWATH_CONCURRENCY, windows.length) },
      async () => {
        while (!signal.aborted) {
          const windowIndex = nextWindowIndex++;
          const window = windows[windowIndex];
          if (!window) return;
          try {
            appendFeatures(await fetchPredictedWindow(window[0], window[1], signal));
          } catch (error) {
            if ((error as Error).name === 'AbortError') return;
            console.warn('Live swath chunk failed:', error);
          }
        }
      },
    );

    const scheduledTask = fetchScheduledLive(rangeStartMs, rangeEndMs, signal)
      .then(appendFeatures)
      .catch((error) => {
        if ((error as Error).name !== 'AbortError') {
          console.warn('Scheduled swath fetch failed (predicted swaths still load):', error);
        }
      });

    await Promise.all([...predictedWorkers, scheduledTask]);

  } catch (e) {
    if ((e as Error).name !== 'AbortError') {
      console.error('fetchSwaths error:', e);
    }
  } finally {
    if (requestGeneration === swathRequestGeneration) {
      setSwathLoading(false);
      if (swathFetchController?.signal === signal) swathFetchController = null;
    }
  }
}

// Swaths are now fetched for the window on screen, so the user-adjustable
// timeRange drives re-fetching (debounced), not just the rarely-changing
// metaTimeRange. Dragging the range is what loads past or further-out swaths.
/** timeRange updates on every drag frame; coalesce them into one request. */
const LIVE_FETCH_DEBOUNCE_MS = 250;
let liveFetchTimer: ReturnType<typeof setTimeout> | null = null;

watch(
  [effectiveVisible, timeRange, metadata],
  () => {
    if (liveFetchTimer) clearTimeout(liveFetchTimer);
    if (!effectiveVisible.value || !metadata.value) {
      swathRequestGeneration++;
      if (swathFetchController) {
        swathFetchController.abort();
        swathFetchController = null;
      }
      setSwathLoading(false);
      swathSource.clear();
      return;
    }
    liveFetchTimer = setTimeout(() => {
      liveFetchTimer = null;
      fetchSwaths();
    }, LIVE_FETCH_DEBOUNCE_MS);
  },
  { immediate: true },
);

// Client-side re-render when user adjusts time slider, satellite selection,
// data source filter, or cloud cover threshold — no network request needed.
watch(
  [timeRange, selectedSatelliteNames, otDataSourceFilter, cloudCoverThreshold],
  () => {
    swathStyleCache.clear();
    baseLayer.changed();
    selectedLayer.changed();
    scheduleCollect();
  },
);

/* ── Popup overlay ── */
const selectedPassData = computed(() => selectedPass.value);
const selectedPassDisplayName = computed(() => {
  const pass = selectedPassData.value;
  if (!pass) return '';
  return getSatelliteDisplayName(pass.name, ororaConstellationExpanded.value);
});
const selectedPassLatency = computed(() => {
  const pass = selectedPassData.value;
  if (!pass) return null;
  return getExpectedLatencyMinutesForPass(pass);
});
const selectedPassDelivery = computed(() => {
  const pass = selectedPassData.value;
  if (!pass) return null;
  return computeExpectedDeliveryForPass(pass);
});
const groupedVisiblePasses = computed(() =>
  collapseOroraConstellationPasses(visiblePasses.value, ororaConstellationExpanded.value, {
    timeBucketReferenceMs: timeRange.value[0],
  }),
);
const selectedPassesForInteraction = computed<VisiblePass[]>(() => {
  if (selectedPassSourceIds.value.length === 0) return [];
  const idSet = new Set(selectedPassSourceIds.value);
  return visiblePasses.value.filter((pass) => idSet.has(pass.id));
});
const selectedContextPassesForInteraction = computed<VisiblePass[]>(() => {
  if (!selectedPassId.value) return [];
  const idSet = new Set(getGroupedSourcePassIds(selectedPassId.value));
  return visiblePasses.value.filter((pass) => idSet.has(pass.id));
});
const hoveredPassesForInteraction = computed<VisiblePass[]>(() => {
  if (hoveredPassSourceIds.value.length === 0) return [];
  const idSet = new Set(hoveredPassSourceIds.value);
  return visiblePasses.value.filter((pass) => idSet.has(pass.id));
});

function getGroupedSourcePassIds(rawPassId: string): string[] {
  const groupedPass = groupedVisiblePasses.value.find((pass) => groupedPassContainsRawId(pass, rawPassId));
  if (!groupedPass || groupedPass.sourcePassIds.length === 0) return [rawPassId];
  return groupedPass.sourcePassIds;
}
const popupVisible = ref(false);
const popupStyle = ref<Record<string, string>>({});
const popupPlacementClass = ref('popup--bottom');
const popupDismissedForPassId = ref<string | null>(null);

/** The POPUP_MARGIN keeps the popup away from viewport edges */
const POPUP_MARGIN = 12;
/** Arrow size must match the CSS border-width for the ::after pseudo-element */
const ARROW_SIZE = 6;

function fmtTime(iso: string): string {
  return formatDateTime(iso);
}

function closePopup() {
  popupVisible.value = false;
  popupDismissedForPassId.value = selectedPassId.value ?? null;
}

/* ── Add / remove layer when map is ready ── */
function addLayer() {
  const m = map.value;
  if (!m) return;
  m.addLayer(baseLayer);
  m.addLayer(selectedLayer);
  m.on('moveend', collectVisiblePasses);
  m.on('moveend', onMapMoveEnd);
  (m as any).on('singleclick', onMapClick);
  (m as any).on('pointermove', onMapHover);
  m.getViewport()?.addEventListener('mouseleave', onViewportLeave);
}

function removeLayer() {
  const m = map.value;
  if (!m) return;
  m.un('moveend', collectVisiblePasses);
  m.un('moveend', onMapMoveEnd);
  (m as any).un('singleclick', onMapClick);
  (m as any).un('pointermove', onMapHover);
  m.getViewport()?.removeEventListener('mouseleave', onViewportLeave);
  m.removeLayer(selectedLayer);
  m.removeLayer(baseLayer);
}

watch(map, (m) => { if (m) addLayer(); }, { immediate: true });
onBeforeUnmount(removeLayer);

/* ── Re-render when interaction state changes (hover/selection) ── */
watch(
  [selectedPassId, hoveredPassId, selectedPassSourceIds, hoveredPassSourceIds],
  () => {
    // Interaction changes don't affect the style cache keys — just re-evaluate.
    baseLayer.changed();
    selectedLayer.changed();
  },
);

/* ── Layer visibility & opacity ── */
watch(effectiveVisible, (vis) => {
  baseLayer.setVisible(vis);
  selectedLayer.setVisible(vis);
  if (!vis) {
    popupVisible.value = false;
    setHoveredPass(null);
  }
}, { immediate: true });
watch(overpassLayerOpacity, (op) => {
  baseLayer.setOpacity(op);
  selectedLayer.setOpacity(1);
  swathStyleCache.clear();
  baseLayer.changed();
  selectedLayer.changed();
}, { immediate: true });

watch(isColorblindFriendly, () => {
  swathStyleCache.clear();
  baseLayer.changed();
  selectedLayer.changed();
  scheduleCollect();
});

/* ── Map click → select overpass + position popup ── */
let lastClickCoord: number[] | null = null;

/** Stores feature geometry centers AND extents so we can position the popup when selecting from list/graph */
const featureCoordMap = new Map<string, { center: number[]; extent: [number, number, number, number] }>();

function onMapClick(evt: MapBrowserEvent<PointerEvent>) {
  if (mapViewMode.value !== 'swath') return;
  const m = map.value;
  if (!m) return;

  const currentSelectedPasses = selectedContextPassesForInteraction.value;
  let foundId: string | null = null;
  m.forEachFeatureAtPixel(
    evt.pixel,
    (feature) => {
      const p = feature.getProperties();
      if (!featurePassesFilter(p)) return;
      // When a pass is selected, dimmed swaths are invisible — skip them for click
      if (currentSelectedPasses.length && !isFeatureWithinAnyPass(p, currentSelectedPasses)) return;
      const matchedPass = findPassForFeatureProps(p);
      foundId = matchedPass?.id ?? null;
      return true; // stop after first hit
    },
    { layerFilter: (l) => l === baseLayer || l === selectedLayer },
  );

  if (foundId && selectedPassSourceIds.value.includes(foundId)) {
    // Deselect (clicking the selected pass, or clicking empty space when selected)
    setSelectedPass(null);
    lastClickCoord = null;
  } else if (foundId) {
    setSelectedPass(foundId);
    lastClickCoord = evt.coordinate;
  } else {
    setSelectedPass(null);
    lastClickCoord = null;
  }
}

function onMapHover(evt: MapBrowserEvent<PointerEvent>) {
  if (mapViewMode.value !== 'swath') return;
  const m = map.value;
  if (!m) return;

  const currentSelectedPasses = selectedContextPassesForInteraction.value;
  let foundId: string | null = null;
  m.forEachFeatureAtPixel(
    evt.pixel,
    (feature) => {
      const p = feature.getProperties();
      if (!featurePassesFilter(p)) return;
      // When a pass is selected, dimmed swaths are invisible — skip them for hover
      if (currentSelectedPasses.length && !isFeatureWithinAnyPass(p, currentSelectedPasses)) return;
      const matchedPass = findPassForFeatureProps(p);
      foundId = matchedPass?.id ?? null;
      return true;
    },
    { layerFilter: (l) => l === baseLayer },
  );

  const hoverSourceIds = !foundId
    ? []
    : selectedPassSourceIds.value.length > 0
      ? [foundId]
      : getGroupedSourcePassIds(foundId);
  setHoveredPass(foundId, hoverSourceIds);
}

function onViewportLeave() {
  setHoveredPass(null);
}

// Show/hide popup overlay when selection changes
watch(selectedPassId, (id) => {
  if (id && id !== popupDismissedForPassId.value) {
    popupDismissedForPassId.value = null;
  }

  if (id) {
    if (popupDismissedForPassId.value === id) {
      popupVisible.value = false;
      return;
    }
    if (lastClickCoord) {
      const coord = lastClickCoord;
      lastClickCoord = null;
      nextTick(() => positionPopup(coord));
    } else {
      const entry = featureCoordMap.get(id);
      if (entry) {
        nextTick(() => positionPopupFromExtent(entry.center, entry.extent));
      } else {
        popupVisible.value = false;
      }
    }
  } else {
    lastClickCoord = null;
    popupVisible.value = false;
    popupDismissedForPassId.value = null;
  }
});

// Reposition popup when map moves (pan/zoom) while a pass is selected
function onMapMoveEnd() {
  if (!selectedPassId.value) return;
  if (popupDismissedForPassId.value === selectedPassId.value) return;
  const entry = featureCoordMap.get(selectedPassId.value);
  if (entry) {
    positionPopupFromExtent(entry.center, entry.extent);
  } else if (lastClickCoord) {
    positionPopup(lastClickCoord);
  }
}

/**
 * Find the best anchor pixel within the visible viewport for a feature.
 * If the center is on-screen, use it. Otherwise, find where the feature extent
 * overlaps the viewport and pick the midpoint of that overlap.
 */
function findVisibleAnchorPixel(
  center: number[],
  extent: [number, number, number, number],
  m: any,
  vpWidth: number,
  vpHeight: number,
): [number, number] | null {
  // Try the center first
  const centerPx = m.getPixelFromCoordinate(center);
  if (centerPx &&
      centerPx[0] >= 0 && centerPx[0] <= vpWidth &&
      centerPx[1] >= 0 && centerPx[1] <= vpHeight) {
    return centerPx;
  }

  // Center is off-screen — compute pixel positions of extent corners
  const topLeft = m.getPixelFromCoordinate([extent[0], extent[3]]);
  const bottomRight = m.getPixelFromCoordinate([extent[2], extent[1]]);
  if (!topLeft || !bottomRight) return null;

  // Feature bounding box in pixel space
  const fMinX = Math.min(topLeft[0], bottomRight[0]);
  const fMaxX = Math.max(topLeft[0], bottomRight[0]);
  const fMinY = Math.min(topLeft[1], bottomRight[1]);
  const fMaxY = Math.max(topLeft[1], bottomRight[1]);

  // Intersection of feature bbox with viewport
  const iMinX = Math.max(0, fMinX);
  const iMaxX = Math.min(vpWidth, fMaxX);
  const iMinY = Math.max(0, fMinY);
  const iMaxY = Math.min(vpHeight, fMaxY);

  // No intersection → feature is completely off-screen
  if (iMinX >= iMaxX || iMinY >= iMaxY) return null;

  // Use the center of the intersection as the anchor
  return [(iMinX + iMaxX) / 2, (iMinY + iMaxY) / 2];
}

/**
 * Position popup from a known on-screen coordinate (e.g. map click).
 */
function positionPopup(coord: number[]) {
  const m = map.value;
  if (!m || !popupEl.value) { popupVisible.value = false; return; }

  const pixel = m.getPixelFromCoordinate(coord);
  if (!pixel) { popupVisible.value = false; return; }

  const viewport = m.getViewport();
  if (!viewport) { popupVisible.value = false; return; }
  const vpRect = viewport.getBoundingClientRect();

  // Clamp to viewport (click coordinates should be in-view, but be safe)
  const anchorX = Math.max(0, Math.min(vpRect.width, pixel[0]));
  const anchorY = Math.max(0, Math.min(vpRect.height, pixel[1]));

  placePopupAtPixel(anchorX, anchorY, vpRect);
}

/**
 * Position popup using the feature extent — finds the best visible anchor
 * even when the geometry center is off-screen.
 */
function positionPopupFromExtent(
  center: number[],
  extent: [number, number, number, number],
) {
  const m = map.value;
  if (!m || !popupEl.value) { popupVisible.value = false; return; }

  const viewport = m.getViewport();
  if (!viewport) { popupVisible.value = false; return; }
  const vpRect = viewport.getBoundingClientRect();

  const anchor = findVisibleAnchorPixel(center, extent, m, vpRect.width, vpRect.height);
  if (!anchor) {
    popupVisible.value = false;
    return;
  }

  placePopupAtPixel(anchor[0], anchor[1], vpRect);
}

/**
 * Core placement logic — given a map-pixel anchor and the viewport rect,
 * measure the popup and position it on the best side, clamped to viewport.
 */
function placePopupAtPixel(anchorMapX: number, anchorMapY: number, vpRect: DOMRect) {
  const screenX = vpRect.left + anchorMapX;
  const screenY = vpRect.top + anchorMapY;

  // Make popup visible first (hidden off-screen) so we can measure it
  popupVisible.value = true;
  popupStyle.value = { left: '-9999px', top: '-9999px' };

  nextTick(() => {
    if (!popupEl.value) return;
    const popupRect = popupEl.value.getBoundingClientRect();
    const pw = popupRect.width;
    const ph = popupRect.height;
    const gap = ARROW_SIZE + 8; // distance between anchor point and popup edge

    // Available space in each direction from the anchor point to the viewport edge
    const spaceAbove = anchorMapY;
    const spaceBelow = vpRect.height - anchorMapY;
    const spaceLeft = anchorMapX;
    const spaceRight = vpRect.width - anchorMapX;

    // Determine best placement based on available space
    type Placement = 'bottom' | 'top' | 'right' | 'left';
    let placement: Placement = 'bottom'; // pointer on bottom of popup = popup above anchor

    // Prefer placing popup above the anchor (pointer on bottom)
    if (spaceAbove >= ph + gap + POPUP_MARGIN) {
      placement = 'bottom';
    } else if (spaceBelow >= ph + gap + POPUP_MARGIN) {
      placement = 'top';
    } else if (spaceRight >= pw + gap + POPUP_MARGIN) {
      placement = 'left';
    } else if (spaceLeft >= pw + gap + POPUP_MARGIN) {
      placement = 'right';
    } else {
      // Fallback: whichever direction has the most space
      const maxSpace = Math.max(spaceAbove, spaceBelow, spaceLeft, spaceRight);
      if (maxSpace === spaceAbove) placement = 'bottom';
      else if (maxSpace === spaceBelow) placement = 'top';
      else if (maxSpace === spaceRight) placement = 'left';
      else placement = 'right';
    }

    let left: number;
    let top: number;

    if (placement === 'bottom') {
      left = screenX - pw / 2;
      top = screenY - ph - gap;
    } else if (placement === 'top') {
      left = screenX - pw / 2;
      top = screenY + gap;
    } else if (placement === 'left') {
      left = screenX + gap;
      top = screenY - ph / 2;
    } else {
      left = screenX - pw - gap;
      top = screenY - ph / 2;
    }

    // Clamp to viewport bounds
    left = Math.max(vpRect.left + POPUP_MARGIN, Math.min(vpRect.right - pw - POPUP_MARGIN, left));
    top = Math.max(vpRect.top + POPUP_MARGIN, Math.min(vpRect.bottom - ph - POPUP_MARGIN, top));

    popupPlacementClass.value = `popup--${placement}`;
    popupStyle.value = {
      position: 'fixed',
      left: `${Math.round(left)}px`,
      top: `${Math.round(top)}px`,
      zIndex: '10000',
    };
  });
}

/* ── Visible pass collection (debounced pixel sampling) ── */
let collectTimer: ReturnType<typeof setTimeout> | null = null;
const MAX_CONTIGUOUS_GAP_MS = 15 * 60 * 1000;

interface SampledSegment {
  key: string;
  name: string;
  startMs: number;
  endMs: number;
  startIso: string;
  endIso: string;
  sensor_type?: string;
  spatial_res_m?: number;
  constellation?: string;
  tasking?: boolean;
  direction?: VisiblePass['direction'];
  is_daytime?: boolean;
  cloud_cover_pct?: number | null;
  extent: [number, number, number, number];
}

interface AggregatedPass {
  name: string;
  startMs: number;
  endMs: number;
  sensor_type?: string;
  spatial_res_m?: number;
  constellation?: string;
  tasking?: boolean;
  direction?: VisiblePass['direction'];
  is_daytime?: boolean;
  cloud_cover_pct?: number | null;
  cloud_sum: number;
  cloud_count: number;
  extent: [number, number, number, number];
}

function buildViewportGeoJson(extent: [number, number, number, number]): Record<string, unknown> {
  const corners = [
    [extent[0], extent[1]],
    [extent[0], extent[3]],
    [extent[2], extent[3]],
    [extent[2], extent[1]],
  ].map(([x, y]) => {
    const [lon, lat] = toLonLat([x, y]);
    return [lon, lat];
  });

  return {
    type: 'Polygon',
    coordinates: [[...corners, corners[0]]],
  };
}

function getViewportPassTimes(
  feature: Feature,
  globalStartMs: number,
  globalEndMs: number,
  viewportGeoJson: Record<string, unknown>,
): [number, number] {
  // Exact intervals are already localized by the response producer. The swath
  // endpoint normally returns orbit-leg times, so those need viewport timing.
  if (feature.get('times_exact') === true) return [globalStartMs, globalEndMs];

  const geometry = feature.getGeometry();
  if (!geometry) return [globalStartMs, globalEndMs];

  try {
    const swathGeoJson = geoJsonFormat.writeGeometryObject(geometry, {
      featureProjection: 'EPSG:3857',
      dataProjection: 'EPSG:4326',
    }) as Record<string, unknown>;
    // The active swath catalog is LEO-only. Full LEO orbit legs can span more
    // than 60 degrees of latitude, so retain the local-contact calculation even
    // when the AOI helper would otherwise classify that width as GEO.
    return clipTimesToAoi(globalStartMs, globalEndMs, swathGeoJson, viewportGeoJson, undefined, true);
  } catch {
    return [globalStartMs, globalEndMs];
  }
}

function toMs(value: unknown): number {
  return parseUtcEpochMs(String(value ?? ''));
}

function satellitePassesFilter(props: Record<string, unknown>): boolean {
  const satName = String(props.satellite ?? '').toUpperCase();
  if (!selectedSatelliteNames.value.some((name) => satName === name.toUpperCase())) return false;
  if (!passWithinForecastHorizon(satName, toMs(props.start_time), String(props.data_source ?? ''))) return false;
  if (isOroraTechConstellationSatellite(satName)) {
    const dataSource = String(props.data_source ?? '');
    const expectedSource = otDataSourceFilter.value === 'predicted' ? 'api' : 'forest_client';
    if (dataSource && dataSource !== expectedSource) return false;
  }
  return true;
}

function unionExtent(
  a: [number, number, number, number],
  b: [number, number, number, number],
): [number, number, number, number] {
  return [
    Math.min(a[0], b[0]),
    Math.min(a[1], b[1]),
    Math.max(a[2], b[2]),
    Math.max(a[3], b[3]),
  ];
}

function isFeatureWithinPass(props: Record<string, unknown>, pass: VisiblePass): boolean {
  if (String(props.satellite ?? '') !== pass.name) return false;
  const passStart = toMs(pass.start_time);
  const passEnd = toMs(pass.end_time);
  const featStart = toMs(props.viewport_start_time ?? props.start_time);
  const featEnd = toMs(props.viewport_end_time ?? props.end_time);
  if (!Number.isFinite(passStart) || !Number.isFinite(passEnd)) return false;
  if (!Number.isFinite(featStart) || !Number.isFinite(featEnd)) return false;
  return featStart <= passEnd && featEnd >= passStart;
}

function isFeatureWithinAnyPass(props: Record<string, unknown>, passes: VisiblePass[]): boolean {
  return passes.some((pass) => isFeatureWithinPass(props, pass));
}

function findPassForFeatureProps(props: Record<string, unknown>): VisiblePass | null {
  const matches = visiblePasses.value.filter((pass) => isFeatureWithinPass(props, pass));
  if (!matches.length) return null;
  return matches.sort((a, b) => Date.parse(a.start_time) - Date.parse(b.start_time))[0];
}

function overlapMs(aStart: number, aEnd: number, bStart: number, bEnd: number): number {
  return Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart));
}

function findReplacementPass(previous: VisiblePass, passes: VisiblePass[]): VisiblePass | null {
  const prevStart = toMs(previous.start_time);
  const prevEnd = toMs(previous.end_time);
  if (!Number.isFinite(prevStart) || !Number.isFinite(prevEnd)) return null;

  const sameSatellite = passes.filter((pass) => pass.name === previous.name);
  if (!sameSatellite.length) return null;

  const prevMid = (prevStart + prevEnd) / 2;
  let best: VisiblePass | null = null;
  let bestOverlap = -1;
  let bestMidpointDelta = Number.POSITIVE_INFINITY;

  for (const candidate of sameSatellite) {
    const candidateStart = toMs(candidate.start_time);
    const candidateEnd = toMs(candidate.end_time);
    if (!Number.isFinite(candidateStart) || !Number.isFinite(candidateEnd)) continue;

    const overlap = overlapMs(prevStart, prevEnd, candidateStart, candidateEnd);
    const midpoint = (candidateStart + candidateEnd) / 2;
    const midpointDelta = Math.abs(midpoint - prevMid);

    if (overlap > bestOverlap || (overlap === bestOverlap && midpointDelta < bestMidpointDelta)) {
      best = candidate;
      bestOverlap = overlap;
      bestMidpointDelta = midpointDelta;
    }
  }

  return best;
}

function scheduleCollect() {
  if (collectTimer) clearTimeout(collectTimer);
  collectTimer = setTimeout(collectVisiblePasses, 200);
}

function collectVisiblePasses() {
  if (mapViewMode.value !== 'swath') return;
  const m = map.value;
  if (!m) return;

  const previousSelected = selectedPass.value;
  const previousSelectedId = selectedPassId.value;

  const view = m.getView();
  const zoom = view.getZoom() ?? 0;

  if (zoom < 4) {
    visiblePassCount.value = -1;
    visiblePasses.value = [];
    swathGraphPasses.value = [];
    return;
  }

  const rawViewExtent = view.calculateExtent(m.getSize());
  const viewExtent: [number, number, number, number] = [
    rawViewExtent[0],
    rawViewExtent[1],
    rawViewExtent[2],
    rawViewExtent[3],
  ];
  const viewportGeoJson = buildViewportGeoJson(viewExtent);

  const sampledSegments = new Map<string, SampledSegment>();

  // Clear old coordinate cache
  featureCoordMap.clear();

  // Use the spatial index for candidates, then require the actual swath
  // geometry to intersect the viewport before it can enter the timeline.
  swathSource.forEachFeatureIntersectingExtent(viewExtent, (feature) => {
    const p = feature.getProperties();
    if (!satellitePassesFilter(p)) return;
    const name = String(p.satellite ?? '');
    const globalStartIso = String(p.start_time ?? '');
    const globalEndIso = String(p.end_time ?? '');
    const globalStartMs = parseUtcEpochMs(globalStartIso);
    const globalEndMs = parseUtcEpochMs(globalEndIso);
    if (!name || !Number.isFinite(globalStartMs) || !Number.isFinite(globalEndMs)) return;

    const geom = feature.getGeometry();
    if (!geom) return;
    const [startMs, endMs] = getViewportPassTimes(feature, globalStartMs, globalEndMs, viewportGeoJson);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return;

    const startIso = new Date(startMs).toISOString();
    const endIso = new Date(endMs).toISOString();
    feature.setProperties({
      viewport_start_time: startIso,
      viewport_end_time: endIso,
    }, true);
    const ext = geom.getExtent();

    const extent: [number, number, number, number] = [ext[0], ext[1], ext[2], ext[3]];
    // De-duplicate repeated geometry using the API interval, while aggregating
    // with the viewport-local interval derived above.
    const segKey = `${name}-${globalStartIso}-${globalEndIso}`;

    const existing = sampledSegments.get(segKey);
    if (!existing) {
      sampledSegments.set(segKey, {
        key: segKey,
        name,
        startMs,
        endMs,
        startIso,
        endIso,
        sensor_type: p.sensor_type ? String(p.sensor_type) : undefined,
        spatial_res_m: p.spatial_res_m != null ? Number(p.spatial_res_m) : undefined,
        constellation: p.constellation ? String(p.constellation) : undefined,
        tasking: p.tasking !== undefined ? Boolean(p.tasking) : undefined,
        direction: normalizePassDirection(p.direction),
        is_daytime: p.is_daytime !== undefined ? Boolean(p.is_daytime) : undefined,
        cloud_cover_pct: p.cloud_cover_pct != null ? Number(p.cloud_cover_pct) : null,
        extent,
      });
    } else {
      existing.extent = unionExtent(existing.extent, extent);
      if (!existing.direction) existing.direction = normalizePassDirection(p.direction);
    }
  });

  const segments = Array.from(sampledSegments.values()).sort((a, b) => {
    if (a.name !== b.name) return a.name.localeCompare(b.name);
    return a.startMs - b.startMs;
  });

  const aggregated: AggregatedPass[] = [];
  for (const seg of segments) {
    const last = aggregated[aggregated.length - 1];
    const canMerge =
      !!last
      && last.name === seg.name
      && seg.startMs <= last.endMs + MAX_CONTIGUOUS_GAP_MS
      && (!last.direction || !seg.direction || last.direction === seg.direction);

    if (canMerge && last) {
      last.endMs = Math.max(last.endMs, seg.endMs);
      last.extent = unionExtent(last.extent, seg.extent);
      if (!last.sensor_type && seg.sensor_type) last.sensor_type = seg.sensor_type;
      if (last.spatial_res_m == null && seg.spatial_res_m != null) last.spatial_res_m = seg.spatial_res_m;
      if (!last.constellation && seg.constellation) last.constellation = seg.constellation;
      if (last.tasking === undefined && seg.tasking !== undefined) last.tasking = seg.tasking;
      if (!last.direction && seg.direction) last.direction = seg.direction;
      if (last.is_daytime === undefined && seg.is_daytime !== undefined) last.is_daytime = seg.is_daytime;
      if (seg.cloud_cover_pct != null && Number.isFinite(seg.cloud_cover_pct)) {
        last.cloud_sum += seg.cloud_cover_pct;
        last.cloud_count += 1;
        last.cloud_cover_pct = Math.round(last.cloud_sum / last.cloud_count);
      }
    } else {
      aggregated.push({
        name: seg.name,
        startMs: seg.startMs,
        endMs: seg.endMs,
        sensor_type: seg.sensor_type,
        spatial_res_m: seg.spatial_res_m,
        constellation: seg.constellation,
        tasking: seg.tasking,
        direction: seg.direction,
        is_daytime: seg.is_daytime,
        cloud_cover_pct: seg.cloud_cover_pct ?? null,
        cloud_sum: seg.cloud_cover_pct != null && Number.isFinite(seg.cloud_cover_pct) ? seg.cloud_cover_pct : 0,
        cloud_count: seg.cloud_cover_pct != null && Number.isFinite(seg.cloud_cover_pct) ? 1 : 0,
        extent: seg.extent,
      });
    }
  }

  // Use the viewport center as the reference location for solar calculations.
  // Extent centroids are unreliable because tile-clipped geometries can produce
  // centroids far from the user's area of interest, causing inconsistent day/night
  // values across satellites that all pass over the same viewport region.
  const rawCenter = view.getCenter() ?? [0, 0];
  const [viewLon, viewLat] = toLonLat(rawCenter);

  const passes: VisiblePass[] = aggregated
    .sort((a, b) => a.startMs - b.startMs)
    .map((item) => {
      const startIso = new Date(item.startMs).toISOString();
      const endIso = new Date(item.endMs).toISOString();
      const id = `${item.name}-${startIso}-${endIso}`;
      const center = [(item.extent[0] + item.extent[2]) / 2, (item.extent[1] + item.extent[3]) / 2];
      featureCoordMap.set(id, { center, extent: item.extent });
      const [, centroidLat] = toLonLat(center);
      return {
        id,
        name: item.name,
        start_time: startIso,
        end_time: endIso,
        sensor_type: item.sensor_type,
        spatial_res_m: item.spatial_res_m,
        constellation: item.constellation,
        tasking: item.tasking,
        direction: item.direction,
        is_daytime: computeIsDaytime(item.startMs, item.endMs, viewLat, viewLon),
        cloud_cover_pct: item.cloud_cover_pct ?? null,
        centroid_lat: centroidLat,
      };
    });

  swathGraphPasses.value = passes;
  const filteredPasses = passes.filter((pass) => {
    const startMs = parseUtcEpochMs(pass.start_time);
    const endMs = parseUtcEpochMs(pass.end_time);
    return !(startMs > timeRange.value[1] || endMs < timeRange.value[0]);
  });

  visiblePassCount.value = filteredPasses.length;
  visiblePasses.value = filteredPasses;

  // Viewport-local timing changes the map z-order as well as the timeline.
  baseLayer.changed();
  selectedLayer.changed();

  if (!previousSelectedId) return;
  if (filteredPasses.some((pass) => pass.id === previousSelectedId)) return;

  const replacement = previousSelected ? findReplacementPass(previousSelected, filteredPasses) : null;
  setSelectedPass(replacement?.id ?? null);
}
</script>
